import { EventManager } from '@core/events/event-manager.ts'
import { TrackedEvent } from '@core/events/tracked-event.ts'
import { isPageUrlRequiredAction } from './sdk-tracked-event-action.ts'
import { Globals } from '../globals.ts'
import { Env } from '../env.ts'

/** @internal */
export class SdkEventManager extends EventManager {
    public _setup() {
        super._setup()
        this._registerPageLifecycleFlush()
    }

    /**
     * The queue's interval timer frequently does not fire before a user navigates away
     * immediately after acting on a prompt, so attribution-critical events
     * (permission/prompt outcomes, impressions) are lost. Flush the queue when the page
     * is hidden / unloading; the request transport uses `keepalive`, so the send
     * survives unload. (NS-2558)
     */
    protected _registerPageLifecycleFlush(): void {
        const doc = Env._document
        doc.addEventListener('visibilitychange', () => {
            if (doc.visibilityState === 'hidden') {
                void this._flushPendingEvents()
            }
        })

        // `pagehide` is the most reliable terminal signal (incl. bfcache) for paths
        // where visibilitychange:hidden does not precede unload.
        Env._scope.addEventListener('pagehide', () => void this._flushPendingEvents())
    }

    protected async _trackEvent(event: TrackedEvent): Promise<void> {
        this._ensurePageUrlProperty(event)
        return super._trackEvent(event)
    }

    protected _ensurePageUrlProperty(event: TrackedEvent): void {
        if (!isPageUrlRequiredAction(event.action)) {
            return
        }

        if (!event.data[Globals._tevDataPageUrlKey]) {
            event.data[Globals._tevDataPageUrlKey] = Env._location.href
        }
    }
}
